import localforage from 'localforage';
import FJPhotoRemoveBgCanvas from '../components/FJMainPreview/FJCreationEditPanel/js/FJPhotoRemoveBgCanvas';
import FJUndoRedoManager from '../core/FJUndoRedoManager';
import FJUtil from '../util/FJUtil';
import FJPoolStore from './FJPoolStore';
import FJMessageStore from './FJMessageStore';
import FJLocalStore from './FJLocalStore';

const _autoLocalforage = localforage.createInstance({
    name: 'photo_bg_auto_remover_storage',
    version: 1.0,
    storeName: 'photo_bg_auto_remover_cache',
    description: 'photo bg auto remover store at local storage.',
});

const _customLocalforage = localforage.createInstance({
    name: 'photo_bg_custom_remover_storage',
    version: 1.0,
    storeName: 'photo_bg_custom_remover_cache',
    description: 'photo bg custom remover store at local storage.',
});

class FJPhotoBgRemoverStore {
    _autoRemoveBgUndoRedoObj = new FJUndoRedoManager();

    addUndo(data, isRedo = false) {
        this._autoRemoveBgUndoRedoObj.addUndo(data, isRedo);
    }

    addRedo(data) {
        this._autoRemoveBgUndoRedoObj.addRedo(data);
    }

    undo() {
        const data = this._autoRemoveBgUndoRedoObj.undo();
        return data;
    }

    redo() {
        const data = this._autoRemoveBgUndoRedoObj.redo();
        return data;
    }

    hasUndo() {
        return this._autoRemoveBgUndoRedoObj.hasUndo();
    }

    hasRedo() {
        return this._autoRemoveBgUndoRedoObj.hasRedo();
    }

    clearUndoRedo() {
        this._autoRemoveBgUndoRedoObj.clear();
    }

    /**
     * @desc 图片转 mask 数据
     * @param {string} imageUrl
     * @returns
     */
    async imageToMaskData(imageUrl) {
        const isCrossOrigin = FJUtil.isCrossOrigin(imageUrl);

        if (isCrossOrigin) {
            // 下载图片 避免缓存跨域问题导致的错误
            imageUrl = await FJUtil.getBlobByUrlSolveCrossOrigin(imageUrl);
        }

        return await new Promise((resolve, reject) => {
            const image = new Image();
            image.src = imageUrl;
            image.onload = () => {
                const canvas = FJPoolStore.getCanvas();
                canvas.width = image.width;
                canvas.height = image.height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(image, 0, 0, image.width, image.height);
                const imageData = ctx.getImageData(0, 0, image.width, image.height);

                const maskData = new ImageData(image.width, image.height);
                for (let i = 0; i < maskData.data.length; i += 4) {
                    const alpha = imageData.data[i + 3];
                    if (alpha > 0) {
                        maskData.data[i] = 255;
                        maskData.data[i + 1] = 255;
                        maskData.data[i + 2] = 255;
                        maskData.data[i + 3] = alpha;
                    } else {
                        maskData.data[i] = 0;
                        maskData.data[i + 1] = 0;
                        maskData.data[i + 2] = 0;
                        maskData.data[i + 3] = 0;
                    }
                }

                FJPoolStore.releaseCanvas(canvas);
                resolve(maskData);
            };

            image.onerror = () => {
                reject();
            };
        });
    }

    // 获取在编辑页中的图片blob
    async getDownloadImageBlobList(downloadList, isAuto = true) {
        try {
            const photoRemoveBgCanvas = new FJPhotoRemoveBgCanvas();
            const blobList = [];
            const canvas = FJPoolStore.getCanvas('2d', true);
            const ctx = canvas.getContext('2d');
            for (const item of downloadList) {
                const { originImgUrl, bgData, strokeData, maskKey } = item;
                await photoRemoveBgCanvas.init(originImgUrl);

                const maskData = isAuto
                    ? await this.getAutoLocalForageItem(maskKey)
                    : await this.getCustomLocalForageItem(maskKey);
                photoRemoveBgCanvas.setMaskData(maskData);
                photoRemoveBgCanvas.setStrokeInfo(strokeData);
                await photoRemoveBgCanvas.setBackgroundInfo(bgData);

                const size = photoRemoveBgCanvas.getSize();
                canvas.width = size.width;
                canvas.height = size.height;
                photoRemoveBgCanvas.draw(
                    ctx,
                    { x: 0, y: 0, width: size.width, height: size.height },
                    { isShowPhotoRemoveBg: true, isNotDrawTransparentBG: true },
                );

                const blob = await new Promise(resolve => {
                    canvas
                        .convertToBlob()
                        .then(blob => {
                            resolve(blob);
                        })
                        .catch(() => {
                            resolve(null);
                        });
                });
                blob.imageSize = { width: size.width, height: size.height };
                blob && blobList.push(blob);
            }
            return blobList;
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    /**
     * @desc 下载图片
     * @param {Array} downloadList
     * @param {boolean} isAuto - 是否是自动去背景
     * @returns
     */
    async downloadImageList(downloadList, isAuto = true) {
        const blobList = await this.getDownloadImageBlobList(downloadList, isAuto).catch(() => {
            FJMessageStore.addMessage(FJLocalStore._('failed-to-download-please-try-again'), 'error');
        });
        try {
            if (blobList) FJUtil.multiDownloadByBlobs(blobList);
        } catch (error) {
            FJMessageStore.addMessage(FJLocalStore._('failed-to-download-please-try-again'), 'error');
            throw error;
        }
    }

    /**
     * @desc 获取自动去背景缓存数据
     * @param {string} key
     * @returns {Promise<ImageData>}
     */
    async getAutoLocalForageItem(key) {
        try {
            return await _autoLocalforage.getItem(key);
        } catch (error) {
            return null;
        }
    }

    /**
     * @desc 获取自定义去背景缓存数据
     * @param {string} key
     * @returns {Promise<ImageData>}
     */
    async getCustomLocalForageItem(key) {
        try {
            return await _customLocalforage.getItem(key);
        } catch (error) {
            return null;
        }
    }

    /**
     * @desc 获取 mask 的 key
     * @param {string} key
     * @returns {string}
     */
    getMaskKey(isReset = false) {
        const newKey = Math.random().toString(36).substring(2, 15);

        if (isReset) {
            return newKey + '_reset';
        }

        return newKey;
    }

    /**
     * @desc 保存自动去背景 mask 数据
     * @param {ImageData} maskData
     * @param {string} key
     * @param {boolean} isReset 是否是重置 mask
     */
    async saveAutoMaskData(maskData, key, isReset = false) {
        try {
            const maskKey = this.getMaskKey(isReset);
            await _autoLocalforage.setItem(maskKey, maskData);

            return maskKey;
        } catch (error) {
            return null;
        }
    }

    /**
     * @desc 保存自定义 mask 数据
     * @param {ImageData} maskData
     * @param {string} key
     * @param {boolean} isReset 是否是重置 mask
     */
    async saveCustomMaskData(maskData, key, isReset = false) {
        try {
            const maskKey = this.getMaskKey(isReset);
            await _customLocalforage.setItem(maskKey, maskData);

            return maskKey;
        } catch (error) {
            return null;
        }
    }

    /**
     * @desc 清除自动去背景缓存
     */
    clearAutoLocalForage() {
        _autoLocalforage.clear();
    }

    /**
     * @desc 清除自定义去背景缓存
     */
    clearCustomLocalForage() {
        _customLocalforage.clear();
    }
}

const FJPhotoBgRemoverStoreSingleton = new FJPhotoBgRemoverStore();
export default FJPhotoBgRemoverStoreSingleton;
